import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  InputBase,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  ClickAwayListener,
} from '@mui/material'
import {
  Search             as SearchIcon,
  PersonOutline      as LeadIcon,
  MonetizationOn     as DealIcon,
  ContactPhone       as ContactIcon,
  Business           as CustomerIcon,
  CheckCircleOutline as TaskIcon,
} from '@mui/icons-material'
import { themeColors } from '../../app/layout/theme'
import {
  searchApi,
  searchResultHref,
  SEARCH_GROUP_LABELS,
  SEARCH_GROUP_ORDER,
  type GlobalSearchResult,
  type SearchEntityType,
} from '../../lib/api/searchApi'

const ENTITY_ICON: Record<SearchEntityType, ReactElement> = {
  Lead:     <LeadIcon sx={{ fontSize: 16 }} />,
  Deal:     <DealIcon sx={{ fontSize: 16 }} />,
  Contact:  <ContactIcon sx={{ fontSize: 16 }} />,
  Customer: <CustomerIcon sx={{ fontSize: 16 }} />,
  Task:     <TaskIcon sx={{ fontSize: 16 }} />,
}

// ── Global search box — top navigation ───────────────────────────────────────
// Real-time, debounced, grouped-by-entity search across Leads/Deals/Contacts/
// Customers/Tasks. All role-based scoping (Org/LOB/owner) happens server-side in
// GlobalSearchQueryHandler — this component only renders whatever comes back.
export function GlobalSearch() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  const [term, setTerm]           = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')
  const [focused, setFocused]     = useState(false)

  // 400ms debounce — matches the existing per-list SearchBar convention.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(term.trim()), 400)
    return () => clearTimeout(t)
  }, [term])

  const enabled = debouncedTerm.length >= 2

  const { data: results = [], isFetching } = useQuery<GlobalSearchResult[]>({
    queryKey:  ['global-search', debouncedTerm],
    queryFn:   () => searchApi.search(debouncedTerm),
    enabled,
    staleTime: 15_000,
  })

  const showDropdown = focused && enabled

  const grouped = SEARCH_GROUP_ORDER
    .map(type => ({ type, items: results.filter(r => r.entityType === type) }))
    .filter(g => g.items.length > 0)

  const closeAndClear = () => {
    setFocused(false)
    setTerm('')
    setDebouncedTerm('')
  }

  const handleSelect = (result: GlobalSearchResult) => {
    closeAndClear()
    inputRef.current?.blur()
    navigate(searchResultHref(result))
  }

  return (
    <ClickAwayListener onClickAway={() => setFocused(false)}>
      <Box sx={{ position: 'relative', width: 433, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: '#FFFFFF',
            border: `1px solid ${focused ? themeColors.primary : themeColors.border}`,
            borderRadius: '8px',
            p: '8px',
            width: '100%',
            height: 36,
            boxSizing: 'border-box',
            transition: 'border-color 0.2s ease',
            boxShadow: focused ? `0 0 0 3px ${themeColors.primary}18` : 'none',
          }}
        >
          {isFetching
            ? <CircularProgress size={16} sx={{ color: themeColors.primary, flexShrink: 0 }} />
            : <SearchIcon sx={{ fontSize: 18, color: focused ? themeColors.primary : '#94a3b8', flexShrink: 0 }} />}
          <InputBase
            inputRef={inputRef}
            placeholder="Search leads, deals, contacts, companies"
            value={term}
            onChange={e => setTerm(e.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={e => {
              if (e.key === 'Escape') { closeAndClear(); inputRef.current?.blur() }
            }}
            sx={{
              fontSize: '13px',
              flex: 1,
              color: '#1e293b',
              '& input::placeholder': { color: '#94a3b8', opacity: 1 },
            }}
          />
        </Box>

        {showDropdown && (
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              width: 480,
              maxHeight: 420,
              overflowY: 'auto',
              borderRadius: '10px',
              border: '1px solid #e8e8f0',
              boxShadow: '0 12px 28px rgba(0,0,0,0.14)',
              zIndex: 200,
            }}
          >
            {isFetching && results.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={20} sx={{ color: themeColors.primary }} />
              </Box>
            ) : grouped.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: themeColors.text.secondary }}>
                  No results for &quot;{debouncedTerm}&quot;
                </Typography>
              </Box>
            ) : (
              grouped.map(group => (
                <Box key={group.type}>
                  <Typography
                    sx={{
                      px: 2, pt: 1.5, pb: 0.5,
                      fontSize: 11, fontWeight: 700,
                      color: themeColors.text.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {SEARCH_GROUP_LABELS[group.type]}
                  </Typography>
                  {group.items.map(item => (
                    <Box
                      key={item.id}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => handleSelect(item)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25,
                        px: 2, py: 1, cursor: 'pointer',
                        '&:hover': { bgcolor: `${themeColors.primary}0D` },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28, height: 28, borderRadius: '7px',
                          bgcolor: `${themeColors.primary}12`, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, color: themeColors.primary,
                        }}
                      >
                        {ENTITY_ICON[item.entityType]}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 13, fontWeight: 600, color: '#1e293b',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11.5, color: themeColors.text.secondary,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {[item.subtitle, item.email, item.phone].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                      </Box>
                      {(item.orgName || item.lobName) && (
                        <Chip
                          label={[item.orgName, item.lobName].filter(Boolean).join(' / ')}
                          size="small"
                          sx={{
                            height: 18, fontSize: 10, bgcolor: '#f0fdf4', color: '#16a34a',
                            fontWeight: 600, border: 'none', flexShrink: 0, maxWidth: 140,
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  )
}
